import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { Star } from '@mui/icons-material';
import { ROUTES } from '@/constants';
import { LANDING_CONTAINER_MAX_WIDTH, landingGridSpacing, landingHeroSpacing } from '@/constants/landingLayout';
import LandingVideoCarousel from '@components/LandingVideoCarousel';

type CondoHeroSectionProps = {
  defaultTrialDays: number;
  onNavigate: (path: string) => void;
};

export default function CondoHeroSection({ defaultTrialDays, onNavigate }: CondoHeroSectionProps) {
  return (
    <Box
      id="hero"
      sx={{
        ...landingHeroSpacing,
        background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
        color: 'text.primary',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH} sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={landingGridSpacing.hero} alignItems="center">
          {/* Coluna esquerda: valor prop + CTAs */}
          <Grid item xs={12} md={7}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.2,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                  color: '#21316a',
                }}
              >
                Gestão inteligente para condomínios
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(33, 49, 106, 0.08)',
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  mb: 3,
                  border: '1px solid rgba(33, 49, 106, 0.2)',
                }}
              >
                <Star sx={{ fontSize: 18, color: '#21316a' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#21316a' }}>
                  Teste Grátis por {defaultTrialDays} {defaultTrialDays === 1 ? 'Dia' : 'Dias'}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  maxWidth: 520,
                  mx: { xs: 'auto', md: 0 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  whiteSpace: 'pre-line',
                }}
              >
                {`Seu condomínio tem problemas. O V-Condo garante que eles não sejam esquecidos.

                Centralize solicitações, tarefas, manutenções e ocorrências em um único lugar, organizando cada demanda do início à conclusão.

                Moradores podem registrar solicitações rapidamente através de QR Codes nas áreas comuns ou formulários públicos, enquanto síndicos e equipes acompanham tudo em tempo real.

                Mais organização para a equipe. Mais controle para o síndico. Mais transparência para o condomínio.`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onNavigate(`${ROUTES.LOGIN}?tab=signup`)}
                  sx={{
                    backgroundColor: '#21316a',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(33, 49, 106, 0.25)',
                    '&:hover': {
                      backgroundColor: '#1a2854',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(33, 49, 106, 0.3)',
                    },
                  }}
                >
                  Começar Teste Grátis
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    borderColor: '#21316a',
                    borderWidth: 2,
                    color: '#21316a',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#1a2854',
                      borderWidth: 2,
                      backgroundColor: 'rgba(33, 49, 106, 0.06)',
                      color: '#1a2854',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Ver Preços
                </Button>
              </Box>
            </Box>
          </Grid>
          {/* Coluna direita: vídeo (hero asset) */}
          <Grid item xs={12} md={5}>
            <LandingVideoCarousel />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}